// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useFetch from "@/hooks/useFetch";
import useLang from "@/hooks/useLang";
import useTranslation from "@/hooks/useTranslation";
import type { TyNote } from "@/types";
import { formatDateTime } from "@/utils";
import Avatar from "@/UI/atoms/Avatar";
import Icon from "@/UI/atoms/Icon";
import TextInput from "@/UI/inputs/TextInput";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
export type TyNoteFormDetails = {
    /** Where the note lives — `/crm/leads/:id` or `/crm/customers/:id`. */
    basePath: string;
    /** The note being read, or `null` to write one. */
    note?: TyNote | null;
    onDone: () => void;
};

type Props = { onClose: () => void; details?: unknown };

/**
 * A note, in full.
 *
 * The card clamps to four lines, so this is the only place a long one can be read — which is why
 * opening it is the card's own click rather than a pencil hiding in its corner.
 *
 * **Editing replaces rather than patches.** The API has no route to change a note's text — a note
 * is signed by whoever wrote it, and letting a second person rewrite the words under the first
 * one's name would make the signature a lie. So an edit removes and re-adds, which re-signs it with
 * the person actually making the change. The two calls run in order and the second only runs if the
 * first lands.
 */
export default function NoteFormPopup({ onClose, details }: Props) {
    const t = useTranslation();
    const lang = useLang((state) => state.lang) ?? "ar";
    const { basePath, note, onDone } = details as TyNoteFormDetails;

    const [text, setText] = useState(note?.text ?? "");
    const author = typeof note?.client === "object" && note.client ? note.client.name : null;

    /**
     * Two calls, chained by their results rather than by `await`: `invoke` queues an effect and
     * returns nothing, so the write only starts once the removal has actually landed. A failed
     * removal therefore leaves the original note standing instead of losing it to a half-done edit.
     */
    const removal = useFetch(`${basePath}/notes`, {}, false);
    const writing = useFetch(`${basePath}/notes`, {}, false);
    const isSaving = removal.loading || writing.loading;

    useEffect(() => {
        if (!removal.succeededAt) return;
        writing.invoke({ method: "POST", path: `${basePath}/notes`, data: { text: text.trim() } });
    }, [removal.succeededAt]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!writing.succeededAt) return;
        onClose();
        onDone();
    }, [writing.succeededAt]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSubmit = () => {
        const next = text.trim();
        if (!next || isSaving) return;

        // Re-signed rather than rewritten — see above.
        if (note) removal.invoke({ method: "DELETE", path: `${basePath}/notes/${note._id}` });
        else writing.invoke({ method: "POST", path: `${basePath}/notes`, data: { text: next } });
    };

    return (
        <section className="side-form side-form--center note-form">
            <header className="side-form__header">
                <h2 className="side-form__title">{note ? t.crm.editNote : t.crm.addNote}</h2>
                <button type="button" className="side-form__close" onClick={onClose} aria-label={t.common.close}>
                    <Icon name="close" size={16} />
                </button>
            </header>

            <div className="side-form__body">
                {note && (
                    <div className="note-form__by">
                        <Avatar name={author ?? "?"} size={32} rounded bordered />
                        <span className="note-form__who">
                            <strong>{author ?? t.crm.sources.catalog}</strong>
                            <span>{formatDateTime(note.createdAt, lang)}</span>
                        </span>
                    </div>
                )}

                <TextInput value={text} onChange={setText} label={t.crm.noteText} multiline rows={7} disabled={isSaving} />

                {(removal.error || writing.error) && <p className="side-form__error">{removal.error ?? writing.error}</p>}
            </div>

            <footer className="side-form__footer">
                <button type="button" className="side-form__cancel" onClick={onClose} disabled={isSaving}>
                    {t.common.cancel}
                </button>
                <button type="button" className="side-form__submit" onClick={handleSubmit} disabled={isSaving || !text.trim()}>
                    {isSaving ? t.common.loading : t.common.save}
                </button>
            </footer>
        </section>
    );
}
